/**
 * useTaskQueriesGraphQL Hooks
 *
 * Apollo Client hooks for task-related queries.
 * Feature: 648-task-triggers (User Story 2)
 */

import { useQuery, gql } from '@apollo/client';
import logger from '@/utils/logger';

/**
 * GraphQL Queries
 */

/**
 * Check if user has a pending COMMENT_ON_PHOTO task for a specific photo in a critique session
 * Used by TaskCreationButton to show pending state
 */
const HAS_PENDING_TASK_FOR_PHOTO_IN_CRITIQUE_QUERY = gql`
  query HasPendingTaskForPhotoInCritique($photoId: ID!, $sessionId: ID!) {
    hasPendingTaskForPhotoInCritique(photoId: $photoId, sessionId: $sessionId)
  }
`;

/**
 * Response type for hasPendingTaskForPhotoInCritique query
 */
interface HasPendingTaskQueryResult {
  hasPendingTaskForPhotoInCritique: boolean;
}

/**
 * Hook to check if user has a pending COMMENT_ON_PHOTO task for a photo in a critique session
 *
 * @param photoId - Global ID of the photo to check
 * @param sessionId - Global ID of the critique session
 * @param options - Optional configuration (skip to disable query)
 * @returns Query result with hasPendingTask boolean
 *
 * @example
 * ```tsx
 * const { hasPendingTask, loading } = useHasPendingTaskForPhotoInCritique(photoId, sessionId);
 * if (hasPendingTask) {
 *   // Show "task pending" state on button
 * }
 * ```
 */
export function useHasPendingTaskForPhotoInCritique(
  photoId: string | null | undefined,
  sessionId: string | null | undefined,
  options?: { skip?: boolean }
) {
  const skip = options?.skip || !photoId || !sessionId;

  const { data, loading, error, refetch } = useQuery<HasPendingTaskQueryResult>(
    HAS_PENDING_TASK_FOR_PHOTO_IN_CRITIQUE_QUERY,
    {
      variables: { photoId, sessionId },
      skip,
      fetchPolicy: 'cache-and-network',
    }
  );

  if (error) {
    logger.error('Tasks', 'Failed to check pending task for photo', { photoId, sessionId, error: error.message });
  }

  return {
    hasPendingTask: data?.hasPendingTaskForPhotoInCritique ?? false,
    loading,
    error,
    refetch,
  };
}
